import type { components } from '../types/api/generated';
import { authHeader } from '@lib/api/auth-header';
import { ffhBackendBaseUrl, ffhClient } from '@lib/api/ffh-client';
import { readAskFfhStream } from '@lib/ask-ffh/stream';
import type { AskFfhStreamEvent } from '@lib/ask-ffh/stream';

const ELIGIBILITY_PATH = '/users/me/chat/eligibility';
const QUESTIONS_PATH = '/users/me/chat/questions';

const HTTP_BAD_REQUEST = 400;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;
const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_SERVICE_UNAVAILABLE = 503;

/** The day's spend ceiling: chat is off until midnight UTC, so retrying won't help. */
const CHAT_PAUSED = 'CHAT_PAUSED';

/** The chat has had its 30 questions; the next one needs a new chat. */
export const CHAT_FULL = 'CHAT_FULL';
/**
 * A retry the backend won't answer under its turn key: the question was never
 * answered and a later one has been asked in its chat since, or the key was
 * first sent with a different question. It can only be asked again as a new
 * question.
 */
export const CHAT_TURN_SUPERSEDED = 'CHAT_TURN_SUPERSEDED';
/** On a follow-up: the chat is another member's, or over 30 days old. */
export const NOT_FOUND = 'NOT_FOUND';

export type AskFfhConversation = components['schemas']['ChatConversation'];
export type AskFfhConversationTurn = components['schemas']['ChatTurn'];

export type AskFfhEligibility = {
  isEligible: boolean;
  /** False when this deployment has no Ask FFH at all (the route is 404). */
  isOffered?: boolean;
  /** Questions left today, when the backend says. */
  questionsLeft?: number;
};

const NOT_ELIGIBLE: AskFfhEligibility = { isEligible: false };
const NOT_OFFERED: AskFfhEligibility = { isEligible: false, isOffered: false };

/**
 * Whether the member may use Ask FFH, as ffh-public-api decides it (FFH-3321).
 * Web's own PostHog flags are site-wide, so the backend makes this call. A 404
 * means the deployment has no chat. Any other failure throws, so a passing
 * outage is retried rather than read as a no.
 */
export const fetchAskFfhEligibility = async (
  accessToken: string
): Promise<AskFfhEligibility> => {
  const response = await fetch(`${ffhBackendBaseUrl()}${ELIGIBILITY_PATH}`, {
    headers: authHeader(accessToken)
  });
  if (response.status === HTTP_NOT_FOUND) return NOT_OFFERED;
  if (!response.ok) {
    throw new Error(`Ask FFH eligibility answered ${response.status}`);
  }
  const body = (await response.json().catch(() => null)) as {
    data?: AskFfhEligibility;
  } | null;
  return body?.data?.isEligible === true ? body.data : NOT_ELIGIBLE;
};

/** Refused before an answer started: the member reads `message` as written. */
export class AskFfhRequestError extends Error {
  readonly canRetry: boolean;
  /** The backend's error code, such as `CHAT_FULL`, when it sent one. */
  readonly code?: string;

  constructor(message: string, canRetry: boolean, code?: string) {
    super(message);
    this.name = 'AskFfhRequestError';
    this.canRetry = canRetry;
    this.code = code;
  }
}

const GENERIC_MESSAGE =
  "Ask FFH can't answer right now. Try again in a moment.";

const STATUS_MESSAGES: Record<number, string> = {
  [HTTP_UNAUTHORIZED]: 'Sign in again to use Ask FFH.',
  [HTTP_NOT_FOUND]: "Ask FFH isn't available right now.",
  [HTTP_SERVICE_UNAVAILABLE]:
    'Ask FFH is busy right now. Try again in a minute.'
};

/** Statuses where asking the same question again will not help. */
const FINAL_STATUSES = new Set([
  HTTP_UNAUTHORIZED,
  HTTP_FORBIDDEN,
  HTTP_NOT_FOUND,
  HTTP_TOO_MANY_REQUESTS
]);

/**
 * Refusals whose message the backend writes for the member, such as when the
 * daily limit resets or that a question is already on the go, so it is shown
 * as sent.
 */
const STATUSES_WITH_MEMBER_MESSAGES = new Set([
  HTTP_FORBIDDEN,
  HTTP_CONFLICT,
  HTTP_TOO_MANY_REQUESTS,
  HTTP_SERVICE_UNAVAILABLE
]);

const CHAT_NOT_FOUND_MESSAGE =
  "We can't find that chat. Start a new one to ask your question.";

/**
 * A follow-up's 404 is about its chat, which has gone, so it gets a message
 * of its own. Any other 404 means the deployment has no chat at all.
 */
const refusalFor = async (
  response: Response,
  isFollowUp: boolean
): Promise<AskFfhRequestError> => {
  const body = (await response.json().catch(() => null)) as {
    error?: { code?: string; message?: string };
  } | null;
  const code = body?.error?.code;
  const isChatGone = isFollowUp && response.status === HTTP_NOT_FOUND;
  const message = isChatGone
    ? CHAT_NOT_FOUND_MESSAGE
    : STATUSES_WITH_MEMBER_MESSAGES.has(response.status) && body?.error?.message
    ? body.error.message
    : STATUS_MESSAGES[response.status] ?? GENERIC_MESSAGE;
  const canRetry = !FINAL_STATUSES.has(response.status) && code !== CHAT_PAUSED;
  return new AskFfhRequestError(
    message,
    canRetry,
    isChatGone ? NOT_FOUND : code
  );
};

/** How a question's stream ended. `cut` means it stopped without saying. */
export type AskFfhOutcome =
  | { kind: 'done' }
  | { kind: 'error'; code: string; message: string }
  | { kind: 'cut' };

/**
 * Asks one question and forwards each stream event as it arrives. The turn
 * key identifies the question across retries, so a retry sends the same one.
 * A conversation id, from an earlier answer's `started` event, makes it a
 * follow-up in that chat; without one it starts a new chat.
 */
export const askFfhQuestion = async ({
  accessToken,
  question,
  turnKey,
  conversationId,
  signal,
  onEvent
}: {
  accessToken: string;
  question: string;
  turnKey: string;
  conversationId?: string | null;
  signal: AbortSignal;
  onEvent: (event: AskFfhStreamEvent) => void;
}): Promise<AskFfhOutcome> => {
  const body: components['schemas']['AskQuestionBody'] = {
    question,
    turnKey,
    ...(conversationId ? { conversationId } : {})
  };
  const response = await fetch(`${ffhBackendBaseUrl()}${QUESTIONS_PATH}`, {
    method: 'POST',
    // No `Accept: text/event-stream`: MSW's service worker passes those
    // requests straight to the network, so the mock scenario never sees them.
    headers: {
      ...authHeader(accessToken),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body),
    signal
  });

  if (!response.ok || !response.body) {
    throw await refusalFor(response, Boolean(conversationId));
  }

  let outcome: AskFfhOutcome = { kind: 'cut' };
  await readAskFfhStream(response.body, (event) => {
    if (event.event === 'done') {
      outcome = { kind: 'done' };
    } else if (event.event === 'error') {
      outcome = { kind: 'error', ...event.data };
    }
    onEvent(event);
  });
  return outcome;
};

/**
 * One of the member's stored chats, to show again after a reload. Null when
 * the backend has no such chat for them (another member's, over 30 days old,
 * or a backend without stored chats), so the page starts a new one.
 */
export const fetchAskFfhConversation = async (
  accessToken: string,
  conversationId: string
): Promise<AskFfhConversation | null> => {
  const { data, error, response } = await ffhClient().GET(
    '/users/me/chat/conversations/{conversationId}',
    {
      params: { path: { conversationId } },
      headers: authHeader(accessToken)
    }
  );
  if (
    response.status === HTTP_NOT_FOUND ||
    response.status === HTTP_BAD_REQUEST
  ) {
    return null;
  }
  if (error || !data?.data) throw new Error('Failed to load the chat');
  return data.data;
};
